import { useState } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { GetStaticProps } from 'next'

import { CaseStudy } from '@ecm/data/CaseStudy'
import { convertToComponent } from '@ecm/data/Contentful'
import { runEff } from '@ecm/effect'
import { SectionType } from '@ecm/ui/component/caseStudies/CaseStudyArticle'
import CaseStudyArticle from '@ecm/ui/component/caseStudies/CaseStudyArticle'
import CaseStudySidebar from '@ecm/ui/component/caseStudies/CaseStudySidebar'
import Breadcrumbs, { BreadCrumb } from '@ecm/ui/element/Breadcrumbs'
import Color from '@ecm/ui/element/Color'
import ContentfulImage, { ImageType } from '@ecm/ui/element/ContentfulImage'
import MainContainer from '@ecm/ui/element/MainContainer'
import DefaultLayout from '@ecm/ui/layout/Default'

type CaseStudyPageProps = {
  caseStudy: CaseStudy
  breadCrumbList: BreadCrumb[]
}

const CaseStudyPage = ({ caseStudy, breadCrumbList }: CaseStudyPageProps) => {
  const [activeSection, setActiveSection] = useState(SectionType.Summary)
  const [isListeningScroll, setListeningScroll] = useState(true)

  const handleNavBtnClick = (id: SectionType) => {
    if (id === activeSection) {
      return
    }
    setListeningScroll(false)
    setActiveSection(id)
  }

  return (
    <DefaultLayout
      seoTitle={`${caseStudy.organizationName} case study | Earth Class Mail`}
      seoDescription={`Explore ${caseStudy.organizationName}'s success story with Earth Class Mail to see how we help them refocus on their business operations and growth.`}
      hideFinalCta
    >
      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 750px)`,
        }}
      >
        <MainContainer sx={{ py: '64px' }}>
          <Breadcrumbs breadCrumbList={breadCrumbList} />
          <Box
            display="grid"
            gridTemplateColumns={{ sm: '1fr 1fr', xs: '1fr' }}
            gap="32px"
            alignItems="center"
            mb="46px"
          >
            <Box>
              <Typography variant="h2" component="h1" mb="16px">
                {caseStudy.header}
              </Typography>
              {convertToComponent(caseStudy.subHeader, 'p3')}
            </Box>

            <ContentfulImage
              imageType={ImageType.CaseStudyHero}
              src={caseStudy.heroImg}
              layout="responsive"
              objectFit="cover"
              alt={`${caseStudy.organizationName} hero image`}
              priority
            />
          </Box>

          <Box
            mt="16px"
            pb="80px"
            display="grid"
            gridTemplateColumns={{ md: '1fr 3fr', xs: '1fr' }}
            gap={{ lg: '128px', md: '80px', xs: '60px' }}
          >
            <CaseStudySidebar
              onClick={handleNavBtnClick}
              activeSection={activeSection}
              logoImg={caseStudy.logoImg}
              organizationName={caseStudy.organizationName}
              industry={caseStudy.industry}
              missionDescription={caseStudy.missionDescription}
              pdfUrl={caseStudy.pdfUrl}
            />
            <CaseStudyArticle
              activeSection={activeSection}
              isListeningScroll={isListeningScroll}
              setListeningScroll={setListeningScroll}
              onScroll={setActiveSection}
              summaryDescription={caseStudy.summaryDescription}
              quote={caseStudy.quote}
              challengesDescription={caseStudy.challengesDescription}
              theProblemAtLargeDescription={caseStudy.theProblemAtLargeDescription}
              solutionDescription={caseStudy.theProblemAtLargeDescription}
              resultsDescription={caseStudy.resultsDescription}
            />
          </Box>
        </MainContainer>
      </Box>
      <Divider sx={{ backgroundColor: Color.smoke.normal }} />
    </DefaultLayout>
  )
}

export default CaseStudyPage

export const getStaticProps: GetStaticProps = async ctx => {
  const slug = ctx.params?.slug as string

  const caseStudy: CaseStudy = await runEff(eff => eff.getCaseStudyBySlug(slug))

  const breadCrumbList: BreadCrumb[] = [
    { description: 'Case studies', href: '/case-studies', position: 2 },
    {
      description: caseStudy.organizationName,
      href: `/case-studies/${caseStudy.organizationName}`,
      position: 3,
    },
  ]

  return { props: { breadCrumbList, caseStudy } }
}

export async function getStaticPaths() {
  const caseStudies: CaseStudy[] = await runEff(eff => eff.getCaseStudies())

  const paths = caseStudies.map(c => ({ params: c }))

  return {
    fallback: false,
    paths,
  }
}
